import { useState, cloneElement } from "react";
import { Card, CardContent } from "@mui/material";
import Button from "@mui/material/Button";
import { ViewSidebar, Add } from "@mui/icons-material";
import DownloadIcon from '@mui/icons-material/GetApp';
import {
  AutocompleteInput,
  BooleanField,
  Datagrid,
  Filter,
  FilterList,
  FilterListItem,
  List,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
} from "react-admin";
import { endOfToday, startOfToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { ImportLicensesButton } from "../../components/import-licenses-button.component";
import { ImportUsersLicensesButton } from "../../components/import-users-licenses-button.component";
import { CreateLicenseDialog } from "../../components/create-license-dialog.component";
import { API } from "../../providers/api.provider";
import { LocalizedDateField } from "../../components/localized-date-field.component";

const LicensesFilter = (props: any) => (
  <Filter {...props}>
    <ReferenceInput
      label="Buscar por CPF/CNPJ"
      reference="users"
      source="userId"
    >
      {/* @ts-ignore */}
      <AutocompleteInput 
        optionText={(choice: any) => choice?.registration || ''}
        shouldRenderSuggestions={(val: string) => val.trim().length > 2}
        filterToQuery={(searchText: string) => ({ registration: searchText })}
      />
    </ReferenceInput>
    <ReferenceInput
      label="Buscar por produto"
      reference="products"
      source="productId"
      sort={{ field: "name", order: "ASC" }}
      filterToQuery={(searchText: string) => ({ name: searchText })}
      allowEmpty={true}
    >
      <SelectInput optionText="name" fullWidth style={{ width: "100%" }} />
    </ReferenceInput>
    <TextInput label="Código do serial" source="serialNumber" />
    <TextInput label="Buscar por email" source="email" />
  </Filter>  
);

const LicensesSidebar = () => (
  <Card style={{ width: 250, marginRight: 16, order: -1 }}>
    <CardContent>
      <FilterList label="Criado" icon={null}>
        <FilterListItem
          label="Hoje"
          value={{
            createdAtStart: startOfToday().toISOString(),
            createdAtEnd: endOfToday().toISOString(),
          }}
        />
        <FilterListItem
          label="Esta semana"
          value={{
            createdAtStart: startOfWeek(new Date()).toISOString(),
            createdAtEnd: endOfWeek(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="Este mês"
          value={{
            createdAtStart: startOfMonth(new Date()).toISOString(),
            createdAtEnd: endOfMonth(new Date()).toISOString(),
          }}
        />
      </FilterList>
      <FilterList label="Atualizado" icon={null}>
        <FilterListItem
          label="Hoje"
          value={{
            updatedAtStart: startOfToday().toISOString(),
            updatedAtEnd: endOfToday().toISOString(),
          }}
        />
        <FilterListItem
          label="Esta semana"
          value={{
            updatedAtStart: startOfWeek(new Date()).toISOString(),
            updatedAtEnd: endOfWeek(new Date()).toISOString(),
          }}
        />
        <FilterListItem
          label="Este mês"
          value={{
            updatedAtStart: startOfMonth(new Date()).toISOString(),
            updatedAtEnd: endOfMonth(new Date()).toISOString(),
          }}
        />
      </FilterList>
    </CardContent>
  </Card>
);

const ListActions = (props: any) => {
  const { className, showSidebar, setShowSidebar, openCreateDialog, setOpenCreateDialog } = props;
  const { filterValues } = useListContext();
  
  const handleExport = async () => {
    const params = new URLSearchParams();
    
    // Adiciona os filtros aos parâmetros da requisição
    Object.keys(filterValues).forEach((key) => {
      if (filterValues[key] !== undefined && filterValues[key] !== null && filterValues[key] !== '') {
        params.append(key, filterValues[key]);
      }
    });
    
    const queryString = params.toString();
    const url = queryString 
      ? `/admin/tasks/export-licenses?${queryString}`
      : '/admin/tasks/export-licenses';
    
    API.get(url, { responseType: "blob" }).then((response) => {
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "licencas.xlsx");
      document.body.appendChild(link);
      link.click();
    });
  };
  
  return (
    <TopToolbar className={className}>
      {cloneElement(props.filters, { context: "button" })}
      <Button
        color="primary"
        onClick={() => setShowSidebar(!showSidebar)}
      >
        <ViewSidebar style={{ marginRight: 8 }} />
        {showSidebar ? 'Ocultar' : 'Mostrar'} Filtros
      </Button>
      <Button
        color="primary"
        onClick={handleExport}
      >
        <DownloadIcon style={{ marginRight: 8 }} />
        Exportar
      </Button>
      <ImportLicensesButton />
      <ImportUsersLicensesButton />
      <Button
        color="primary"
        onClick={() => setOpenCreateDialog(true)}
      >
        <Add style={{ marginRight: 8 }} />
        Criar
      </Button>
      <CreateLicenseDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
      />
    </TopToolbar>
  );
};

export const LicenseList = (props: any) => {
  const [showSidebar, setShowSidebar] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  
  return (
  <List 
    {...props} 
    filters={<LicensesFilter />} 
    actions={
      <ListActions 
        showSidebar={showSidebar} 
        setShowSidebar={setShowSidebar}
        openCreateDialog={openCreateDialog}
        setOpenCreateDialog={setOpenCreateDialog}
      />
    }
    aside={showSidebar ? <LicensesSidebar /> : null}
  >
    <Datagrid rowClick="edit">
      <ReferenceField label="Usuário" reference="users" source="userId" sortable={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="CPF/CNPJ" reference="users" source="userId" sortable={false}>
        <TextField source="registration" />
      </ReferenceField>
      <ReferenceField label="Produto" reference="products" source="productId" sortable={false}>
        <TextField source="name" />
      </ReferenceField>
      <NumberField label="Versão" source="version" />
      <NumberField label="Tempo de uso" source="usageTime" />
      <LocalizedDateField label="Adquirido" source="acquisition" showTime={false} />
      <NumberField label="Ativações contratadas" source="contractedActivations" />
      <NumberField label="Ativações restantes" source="remainingActivations" />
      <TextField label="Código do serial" source="serialNumber" />
      <BooleanField label="Plug" source="plug" />
      <NumberField label="Valor Venda" source="valorVenda" options={{ style: 'currency', currency: 'BRL' }} />
      <NumberField label="Parcelas Vendas" source="parcelasVendas" />
      <TextField label="Método de Pagamento" source="metodoPagamento" />
      <TextField label="Nota Fiscal" source="notaFiscal" />
    </Datagrid>
  </List>
  );
};
